import { NestedKey } from '@40seas/entities/common';
import merge from 'lodash/merge';
import { QueryOperator } from '../entities/query-operator.entity';
import { QueryDto, QueryFilter } from '../entities/query.entity';

export function getQueryFilter(filter: Record<string, any> | undefined): QueryDto<unknown>['filter'] {
    if (!filter || !Object.keys(filter).length) {
        return undefined;
    }

    return Object.entries(filter).reduce((result, [key, value]) => {
        if (value === undefined) {
            return result;
        }

        if (typeof value !== 'string') {
            const [field, operator] = key.split('__');
            if (operator) {
                result[field] = { [operator]: value };
                return result;
            }

            result[key] = value;
            return result;
        }

        // We currently support only one type of 'OR' operation
        // (which means you cannot use two different 'OR' operations within the same query).
        if (key.includes('/')) {
            const keys = key.split('/');
            const orArray = keys.map((k) =>
                recursiveCompoundKey(typeof value === 'object' && !((value as any) instanceof Array), value, k),
            );
            result['or'] = orArray;
        } else {
            const compoundKey = recursiveCompoundKey(typeof value === 'object' && !((value as any) instanceof Array), value, key);
            result = merge(result, compoundKey);
        }
        return result;
    }, {} as Record<string, any>);
}

function recursiveCompoundKey(nonArrayObj: boolean, value: string | any, result: string): any {
    if (!nonArrayObj) {
        const [target, param] = result.split('__');
        return { [target]: { [param]: value } };
    }
    const firstKey = Object.keys(value)[0];

    if (firstKey.includes('__')) {
        return CompoundKeyWithMultipleOperators(firstKey, value, result);
    }

    const compoundValue = value[firstKey];
    return recursiveCompoundKey(
        typeof compoundValue === 'object' && !(compoundValue instanceof Array),
        compoundValue,
        `${result}.${firstKey}`,
    );
}

function CompoundKeyWithMultipleOperators(key: string, value: string | any, basePath: string): any {
    if (key.includes('__')) {
        const [compoundKey] = key.split('__');

        const operatorValues = {};

        Object.keys(value)
            .filter((key) => key.includes('__') && key.split('__')[0] === compoundKey)
            .forEach((key) => {
                const [currentCompoundKey, currentCompoundParam] = key.split('__');
                const currentCompoundValue = value[key];

                Object.assign(operatorValues, { [currentCompoundParam]: currentCompoundValue });
            });

        return { [`${basePath}.${compoundKey}`]: operatorValues };
    }
}

function getQueryPagination(pagination: any, field: 'perPage' | 'page') {
    if (!pagination) {
        return undefined;
    }
    const value = +pagination[field];
    return Number.isFinite(value) ? value : undefined;
}

function getQuerySort(sort: any) {
    if (!sort?.field || sort.field === 'id') {
        return undefined;
    }

    return {
        [sort.field]: sort.order === 'DESC' ? -1 : 1,
    };
}

function serializeQueryFilter(prefix: string, filter: QueryDto<unknown>['filter']): string {
    const serialize = (key: any, operator: any, params: any) => {
        if (params === undefined || params === null) {
            return undefined;
        }

        if (['in', 'nin'].includes(operator)) {
            return params
                .split('%2C')
                .map((param: string) => `${prefix}[${key}][${operator}][]=${param}`)
                .join('&');
        }

        return `${prefix}[${key}][${operator}]=${params}`;
    };

    return Object.entries(filter || {})
        .map(([key, value]) => {
            if (value instanceof Array) {
                return handleArrayFilter(key, value);
            }

            // If we want to send metadata as a object filter, we will need to use this
            if (key === 'metadata') {
                return serializeQueryFilter('filter[metadata]', value as QueryFilter<unknown>);
            }

            return Object.entries(value as QueryOperator).map(([operator, params]) => {
                if (params === undefined || params === null) {
                    return undefined;
                }

                if (operator === 'search') {
                    const serializedFilter = serialize(key, 'regex', encodeURIComponent(params.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&')));
                    return [serializedFilter, serialize(key, 'options', 'i')].join('&');
                } else if (operator === 'regex') {
                    const serializedFilter = serialize(key, operator, encodeURIComponent(params));
                    return [serializedFilter, serialize(key, 'options', 'i')].join('&');
                }
                return serialize(key, operator, encodeURIComponent(params));
            });
        })
        .flat()
        .join('&');
}

function handleArrayFilter(key: string, filters: any[]): any {
    return filters
        .map((filter: any, index: number) => {
            return serializeQueryFilter(`filter[${key}][${index}]`, filter);
        })
        .join('&');
}

function serializeQuerySort(sort?: { [field: string]: 1 | -1 }) {
    if (!sort) {
        return '';
    }
    const [key, value] = Object.entries(sort)[0];
    return `sort[${key}]=${value}`;
}

function serializeParam(key: string, value: string | number | boolean | undefined) {
    if (value === undefined) {
        return '';
    }
    return `${key}=${value}`;
}

function serializeAggregate(aggregate?: QueryDto<unknown>['aggregate']) {
    if (!aggregate) {
        return '';
    }
    const { groupBy, sum, count } = aggregate;
    return [
        groupBy?.map((key) => serializeParam('aggregate[groupBy][]', key)).join('&'),
        serializeParam('aggregate[sum]', sum),
        serializeParam('aggregate[count]', count),
    ]
        .filter(Boolean)
        .join('&');
}

function serializeExpand(expand?: NestedKey<unknown>[] | undefined) {
    if (!expand || !expand.length) {
        return '';
    }
    return expand.map((e) => `expand[]=${e}`).join('&');
}

function serializeQuery(query: QueryDto<unknown>): string {
    const { sort, limit, filter, page, expand, distinctBy, aggregate } = query;
    return [
        serializeQuerySort(sort),
        serializeParam('limit', limit),
        serializeParam('page', page),
        serializeQueryFilter('filter', filter),
        serializeExpand(expand),
        serializeAggregate(aggregate),
        serializeParam('distinctBy', distinctBy),
    ]
        .filter(Boolean)
        .join('&');
}

export function createQuery({
    filter,
    pagination,
    sort,
    expand,
    distinctBy,
    aggregate,
}: {
    pagination?: any;
    filter?: { [key: string]: string | object };
    sort?: any;
    expand?: string[];
    aggregate?: {
        groupBy: string[];
        sum?: string;
        count?: boolean;
    };
    distinctBy?: string;
}): string {
    const query: QueryDto<unknown> = {
        filter: getQueryFilter(filter),
        sort: getQuerySort(sort),
        limit: getQueryPagination(pagination, 'perPage'),
        page: getQueryPagination(pagination, 'page'),
        aggregate: aggregate as QueryDto<unknown>['aggregate'],
        expand: expand as NestedKey<unknown>[] | undefined,
        distinctBy: distinctBy as NestedKey<unknown> | undefined,
    };

    return serializeQuery(query);
}
